<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  formatCurrency: {
    type: Function,
    default: null,
  },
});

const { t, locale } = useI18n();
const browserLocale = computed(() => locale.value.replace('_', '-'));
const { currency } = useCurrency();

const defaultFormatCurrency = value => {
  return new Intl.NumberFormat(browserLocale.value, {
    style: 'currency',
    currency: currency.value,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const currencyFormatter = computed(
  () => props.formatCurrency || defaultFormatCurrency
);

const formatCompact = value => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return currencyFormatter.value(value);
};

const weightedPercentage = computed(() => {
  if (!props.data?.total_pipeline_value) return 0;
  return Math.round(
    (props.data.total_weighted_value / props.data.total_pipeline_value) * 100
  );
});

const stagesList = computed(() => {
  if (!props.data?.by_stage) return [];
  return Object.entries(props.data.by_stage).map(([key, stage]) => ({
    key,
    ...stage,
  }));
});
</script>

<template>
  <div
    class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
  >
    <div class="flex items-center justify-between mb-6">
      <div>
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ t('DEAL_REPORTS.FORECAST.TITLE') }}
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400">
          {{ t('DEAL_REPORTS.FORECAST.SUBTITLE') }}
        </p>
      </div>
      <div v-if="data" class="text-right">
        <p class="text-2xl font-bold text-green-600 dark:text-green-400">
          {{ formatCompact(data.total_weighted_value) }}
        </p>
        <p class="text-xs text-slate-500 dark:text-slate-400">
          {{ weightedPercentage }}% {{ t('DEAL_REPORTS.FORECAST.OF_PIPELINE') }}
        </p>
      </div>
    </div>

    <!-- Content -->
    <div v-if="data" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-3 gap-4">
        <div class="p-4 rounded-lg bg-slate-50 dark:bg-slate-700/50">
          <p class="text-xs text-slate-500 dark:text-slate-400 mb-1">
            {{ t('DEAL_REPORTS.FORECAST.TOTAL_PIPELINE') }}
          </p>
          <p class="text-lg font-semibold text-slate-900 dark:text-white">
            {{ currencyFormatter(data.total_pipeline_value) }}
          </p>
        </div>
        <div class="p-4 rounded-lg bg-green-50 dark:bg-green-900/20">
          <p class="text-xs text-green-600 dark:text-green-400 mb-1">
            {{ t('DEAL_REPORTS.FORECAST.WEIGHTED_VALUE') }}
          </p>
          <p class="text-lg font-semibold text-green-700 dark:text-green-300">
            {{ currencyFormatter(data.total_weighted_value) }}
          </p>
        </div>
        <div class="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20">
          <p class="text-xs text-blue-600 dark:text-blue-400 mb-1">
            {{ t('DEAL_REPORTS.FORECAST.OPEN_DEALS') }}
          </p>
          <p class="text-lg font-semibold text-blue-700 dark:text-blue-300">
            {{ data.open_deals_count }}
          </p>
        </div>
      </div>

      <!-- Stages Breakdown -->
      <div v-if="stagesList.length" class="space-y-3">
        <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300">
          {{ t('DEAL_REPORTS.FORECAST.BY_STAGE') }}
        </h4>
        <div
          v-for="stage in stagesList"
          :key="stage.key"
          class="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-700 last:border-0"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm text-slate-700 dark:text-slate-300">
              {{ stage.label }}
            </span>
            <span class="text-xs text-slate-500 dark:text-slate-400">
              ({{ stage.deals_count }} {{ t('DEAL_REPORTS.FORECAST.DEALS') }})
            </span>
          </div>
          <div class="text-right">
            <p class="text-sm font-medium text-slate-900 dark:text-white">
              {{ currencyFormatter(stage.weighted_value) }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ stage.probability }}% prob.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex items-center justify-center h-48">
      <p class="text-slate-500 dark:text-slate-400">
        {{ t('DEAL_REPORTS.NO_DATA') }}
      </p>
    </div>
  </div>
</template>
